/** Epic 94 — single source for grouped recurring-risk workflow step labels (display only). */

import type { SafetyWorkflowStepId } from '../safetyWorkflowStepMapping';
import type { GroupedCorrectiveWorkflowPhase } from './groupedCorrectiveActionWorkflow';
import { groupedCorrectiveWorkflowPhaseLabel } from './groupedCorrectiveActionWorkflow';

export const GROUPED_SAFETY_CASE_STEP_LABELS = {
  riskPattern: 'Risk pattern',
  evidenceReview: 'Evidence review',
  correctiveAction: 'Corrective action',
  impactTracking: 'Impact measurement',
  safetyReport: 'Report preparation',
} as const;

/** Top-of-page helper for grouped recurring-risk review (warehouse demo). */
export const GROUPED_REVIEW_PAGE_SCOPE_LINE =
  'Review the recurring risk pattern, inspect representative evidence, then decide whether this requires ongoing management.';

export const GROUPED_SAFETY_CASE_WORKFLOW_INTRO =
  'Review the recurring risk, inspect representative evidence, then decide whether this pattern requires ongoing management.';

export const GROUPED_CORRECTIVE_ACTION_LOCKED_DESCRIPTION =
  'Complete evidence review before choosing a corrective action.';

export const GROUPED_CORRECTIVE_ACTION_UNLOCKED_DESCRIPTION =
  'Assign an owner and define the corrective action for this recurring risk.';

export const GROUPED_IMPACT_TRACKING_LOCKED_DESCRIPTION =
  'Locked until corrective follow-up has started.';

export const GROUPED_IMPACT_TRACKING_UNLOCKED_DESCRIPTION =
  'Record the follow-up observation for this recurring risk pattern in the selected review window. This is not multi-day trend proof from a short clip.';

export const GROUPED_SAFETY_CASE_WORKFLOW_INTRO_COMPACT =
  'Review the recurring risk and representative evidence, then decide whether this pattern requires management.';

/** Detail copy when the Safety Report is not yet available (honest gating). */
export const GROUPED_SAFETY_REPORT_BLOCKED_SUMMARY =
  'Report preparation stays blocked until corrective action is completed or validated and impact measurement is completed or explicitly waived.';

export type GroupedWorkflowStepperVisualStatus =
  | 'complete'
  | 'current'
  | 'upcoming'
  | 'locked'
  | 'blocked';

/** Stepper status captions for grouped recurring-risk review (not generic Done/Blocked everywhere). */
export function groupedWorkflowStepperStatusCaption(
  stepId: SafetyWorkflowStepId,
  status: GroupedWorkflowStepperVisualStatus,
  correctiveWorkflowPhase?: GroupedCorrectiveWorkflowPhase,
): string {
  if (stepId === 'detected' && status === 'complete') return 'Identified';
  if (stepId === 'assign_action' && correctiveWorkflowPhase) {
    if (status === 'locked' || correctiveWorkflowPhase === 'locked') return 'Locked';
    return groupedCorrectiveWorkflowPhaseLabel(correctiveWorkflowPhase);
  }
  if (stepId === 'assign_action' && status === 'complete') return 'Completed';
  if (stepId === 'management_decision' && status === 'current') return 'Current';
  if (stepId === 'managed_in_case' && status === 'current') return 'Current';
  if (status === 'complete') return 'Done';
  if (status === 'current') return 'Current';
  if (status === 'locked') return 'Locked';
  if (status === 'blocked') {
    if (stepId === 'report') return 'Pending required steps';
    return 'Blocked';
  }
  return 'Pending';
}
